import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Users2,
  Calendar,
  ClipboardCheck,
  CreditCard,
  MessageSquare,
  BookOpen,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronRight,
  Dumbbell,
  X,
  Building2,
  Building,
  Shield,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { Role } from '@/types'

interface NavItem {
  label:    string
  icon:     React.ReactNode
  href?:    string
  children?: NavItem[]
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon:  <LayoutDashboard className="h-4.5 w-4.5" />,
    href:  '/dashboard',
  },
  {
    label: 'Öğrenciler',
    icon:  <Users className="h-4.5 w-4.5" />,
    href:  '/ogrenciler',
  },
  {
    label: 'Kullanıcılar',
    icon:  <Users2 className="h-4.5 w-4.5" />,
    href:  '/kullanicilar',
  },
  {
    label: 'Şubeler',
    icon:  <Building className="h-4.5 w-4.5" />,
    href:  '/subeler',
  },
  {
    label: 'Gruplar & Dersler',
    icon:  <Calendar className="h-4.5 w-4.5" />,
    children: [
      { label: 'Gruplar',  icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/gruplar' },
      { label: 'Dersler',  icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/dersler' },
      { label: 'Takvim',   icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/takvim' },
    ],
  },
  {
    label: 'Yoklama',
    icon:  <ClipboardCheck className="h-4.5 w-4.5" />,
    children: [
      { label: 'Yoklama Al',      icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/yoklama' },
      { label: 'Yoklama Geçmişi', icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/yoklama/rapor' },
    ],
  },
  {
    label: 'Ödemeler',
    icon:  <CreditCard className="h-4.5 w-4.5" />,
    href:  '/odemeler',
  },
  {
    label: 'Mesajlaşma',
    icon:  <MessageSquare className="h-4.5 w-4.5" />,
    href:  '/mesajlar',
  },
  {
    label: 'Muhasebe',
    icon:  <BookOpen className="h-4.5 w-4.5" />,
    href:  '/muhasebe',
  },
  {
    label: 'Raporlar',
    icon:  <BarChart3 className="h-4.5 w-4.5" />,
    href:  '/raporlar',
  },
  {
    label: 'Ayarlar',
    icon:  <Settings className="h-4.5 w-4.5" />,
    children: [
      { label: 'Okul Bilgileri',  icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/ayarlar?tab=general' },
      { label: 'Kullanıcılar',    icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/ayarlar?tab=users' },
      { label: 'Şubeler',         icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/ayarlar?tab=branches' },
      { label: 'Entegrasyonlar',  icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/ayarlar?tab=subscription' },
    ],
  },
]

// Trainer — only: groups, lessons/calendar, attendance
const trainerNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon:  <LayoutDashboard className="h-4.5 w-4.5" />,
    href:  '/dashboard',
  },
  {
    label: 'Gruplarım',
    icon:  <Users className="h-4.5 w-4.5" />,
    href:  '/gruplar',
  },
  {
    label: 'Ders Programı',
    icon:  <Calendar className="h-4.5 w-4.5" />,
    children: [
      { label: 'Dersler', icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/dersler' },
      { label: 'Takvim',  icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/takvim' },
    ],
  },
  {
    label: 'Yoklama',
    icon:  <ClipboardCheck className="h-4.5 w-4.5" />,
    children: [
      { label: 'Yoklama Al',      icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/yoklama' },
      { label: 'Yoklama Geçmişi', icon: <div className="h-1.5 w-1.5 rounded-full bg-current" />, href: '/yoklama/rapor' },
    ],
  },
]

// ─── NavItem component ────────────────────────────────────────────────────────
interface NavItemProps {
  item:       NavItem
  collapsed:  boolean
  depth?:     number
}

const NavItemRow: React.FC<NavItemProps> = ({ item, collapsed, depth = 0 }) => {
  const location = useLocation()
  const hasChildren = item.children && item.children.length > 0

  // Auto-open parent if a child is active
  const isChildActive = item.children?.some((c) => c.href && location.pathname.startsWith(c.href))
  const [open, setOpen] = useState(!!isChildActive)

  const isActive = item.href
    ? location.pathname === item.href || location.pathname.startsWith(item.href + '/')
    : false

  const baseClass = cn(
    'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg',
    'text-sm font-medium',
    'transition-all duration-150 cursor-pointer',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
    depth === 0 ? 'group' : 'ml-5',
  )

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setOpen((v) => !v)}
          className={cn(
            baseClass,
            isChildActive || isActive
              ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-100',
          )}
          aria-expanded={open}
          title={collapsed ? item.label : undefined}
        >
          <span className="flex-shrink-0" aria-hidden="true">
            {item.icon}
          </span>
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{item.label}</span>
              {open ? (
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
              )}
            </>
          )}
        </button>

        {!collapsed && open && (
          <div className="mt-0.5 space-y-0.5" role="group" aria-label={item.label}>
            {item.children!.map((child) => (
              <NavItemRow key={child.href} item={child} collapsed={false} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <NavLink
      to={item.href!}
      end={item.href === '/dashboard'}
      title={collapsed ? item.label : undefined}
      className={({ isActive: active }) =>
        cn(
          baseClass,
          active
            ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-100',
        )
      }
    >
      <span className="flex-shrink-0" aria-hidden="true">{item.icon}</span>
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
interface SidebarProps {
  collapsed:   boolean
  mobileOpen:  boolean
  onMobileClose: () => void
}

// ─── SuperAdmin nav items ─────────────────────────────────────────────────────
const superAdminNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon:  <BarChart3 className="h-4.5 w-4.5" />,
    href:  '/superadmin',
  },
  {
    label: 'Tenantlar',
    icon:  <Building2 className="h-4.5 w-4.5" />,
    href:  '/superadmin/tenants',
  },
  {
    label: 'Abonelikler',
    icon:  <CreditCard className="h-4.5 w-4.5" />,
    href:  '/superadmin/subscriptions',
  },
]

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, mobileOpen, onMobileClose }) => {
  const { user } = useAuthStore()
  const isSuperAdmin = user?.role === Role.SuperAdmin
  const isTrainer    = user?.role === Role.Trainer

  // Trainers see a stripped-down menu; everyone else sees the full menu
  const activeNavItems = isTrainer ? trainerNavItems : navItems

  return (
  <>
    {/* Mobile overlay */}
    {mobileOpen && (
      <div
        className="fixed inset-0 z-sidebar bg-black/40 backdrop-blur-sm lg:hidden"
        onClick={onMobileClose}
        aria-hidden="true"
      />
    )}

    {/* Sidebar panel */}
    <aside
      className={cn(
        'fixed top-0 left-0 h-screen z-sidebar',
        'bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700',
        'flex flex-col shadow-sidebar',
        'transition-all duration-300 ease-in-out',
        // Desktop: collapsed/expanded
        collapsed ? 'lg:w-[72px]' : 'lg:w-[260px]',
        // Mobile: slide in/out
        mobileOpen ? 'w-[260px] translate-x-0' : 'w-[260px] -translate-x-full lg:translate-x-0',
      )}
      aria-label="Yan menü"
      role="navigation"
    >
      {/* Logo */}
      <div
        className={cn(
          'flex items-center gap-3 h-16 px-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0',
          collapsed ? 'justify-center px-2' : '',
        )}
      >
        <div className="flex-shrink-0 h-8 w-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-sm">
          <Dumbbell className="h-4 w-4 text-white" aria-hidden="true" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
              SporOkulu
            </p>
            <p className="text-xs text-slate-400 truncate">
              {isTrainer ? 'Antrenör Paneli' : 'Yönetim Paneli'}
            </p>
          </div>
        )}
        {/* Mobile close */}
        <button
          onClick={onMobileClose}
          className="ml-auto lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          aria-label="Menüyü kapat"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5" aria-label="Ana menü">
        {activeNavItems.map((item) => (
          <NavItemRow key={item.label} item={item} collapsed={collapsed} />
        ))}

        {/* SuperAdmin section */}
        {isSuperAdmin && (
          <>
            {!collapsed && (
              <div className="px-3 pt-4 pb-1">
                <p className="text-2xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                  <Shield className="h-3 w-3" aria-hidden="true" />
                  Platform Yönetimi
                </p>
              </div>
            )}
            {superAdminNavItems.map((item) => (
              <NavItemRow key={item.label} item={item} collapsed={collapsed} />
            ))}
          </>
        )}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <p className="text-xs text-slate-400 dark:text-slate-500">v0.1.0 · SporOkulu SaaS</p>
        </div>
      )}
    </aside>
  </>
  )
}
